import {containsNonStandardAminoAcids} from 'biojs/Util';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  getMaxAminoAcidCount,
  getMinAminoAcidCount,
  getMaxMultimerComponents,
  getMaxTokenCount,
} from 'model-hub/scientific-models/ScientificModelConstants';
import {assertNever} from 'util/Types';

export interface ComponentPermissions {
  readable: boolean;
  polymerFeaturesEditable?: boolean | null;
}

export interface ComponentData {
  aminoAcids: string;
  count: number;
  numHeavyAtoms: number;
  entityType: ModelTypeConstants;
  permissions: ComponentPermissions;
}

export interface SelectedComponents {
  [componentId: string]: ComponentData;
}

export function calculateTotalAminoAcidCount(components: SelectedComponents): number {
  const {proteinComponents} = separateComponentsByEntityType(components);
  return Object.values(proteinComponents).reduce(
    (total, component) => total + component.aminoAcids.length * component.count,
    0
  );
}

export function calculateTotalHeavyAtomCount(components: SelectedComponents): number {
  const {moleculeComponents} = separateComponentsByEntityType(components);
  return Object.values(moleculeComponents).reduce(
    (total, component) => total + component.numHeavyAtoms * component.count,
    0
  );
}

export function calculateTotalTokenCount(components: SelectedComponents): number {
  const aminoAcidCount = calculateTotalAminoAcidCount(components);
  const heavyAtomCount = calculateTotalHeavyAtomCount(components);
  return aminoAcidCount + heavyAtomCount;
}

export const separateComponentsByEntityType = (
  components: SelectedComponents
): {
  moleculeComponents: SelectedComponents;
  proteinComponents: SelectedComponents;
} => ({
  moleculeComponents: Object.fromEntries(
    Object.entries(components).filter(([, component]) => component.entityType === ModelTypeConstants.MOLECULE)
  ),
  proteinComponents: Object.fromEntries(
    Object.entries(components).filter(([, component]) => component.entityType === ModelTypeConstants.PROTEIN)
  ),
});

export function isComponentSelectionValid(components: SelectedComponents, model: string): boolean {
  return getComponentValidationResult(components, model) == null;
}

export function flattenComponentSelectionToComponentIds(components: SelectedComponents): {
  proteinIds: Array<string>;
  moleculeIds: Array<string>;
} {
  const proteinIds: Array<string> = [];
  const moleculeIds: Array<string> = [];

  Object.entries(components).forEach(([componentId, component]) => {
    const targetArray = component.entityType === ModelTypeConstants.PROTEIN ? proteinIds : moleculeIds;
    targetArray.push(...Array(component.count).fill(componentId));
  });
  return {proteinIds, moleculeIds};
}

export type ValidationError =
  | {
      type: 'no_components';
    }
  | {
      type: 'no_aa_sequence_components';
    }
  | {
      type: 'empty_amino_acids';
      componentIds: Array<string>;
    }
  | {
      type: 'non_standard_amino_acids';
      componentIds: Array<string>;
    }
  | {
      type: 'count_exceeded';
      totalCount: number;
    }
  | {
      type: 'count_too_low';
      totalCount: number;
    }
  | {
      type: 'too_many_components';
      componentCount: number;
    }
  | {
      type: 'too_many_tokens';
      tokenCount: number;
    }
  | {
      type: 'insufficient_permissions';
      componentIds: Array<string>;
    };

export type ComponentValidationResult = Array<ValidationError>;

export const getValidationErrorMessages = (errors: ComponentValidationResult): Array<string> =>
  errors.map((error: ValidationError): string => {
    switch (error.type) {
      case 'no_components':
        return 'No components selected.';
      case 'no_aa_sequence_components':
        return 'No AA sequence components selected.';
      case 'empty_amino_acids':
        return 'At least one AA sequence component does not contain any residues.';
      case 'non_standard_amino_acids':
        return 'At least one AA sequence component has non-standard amino acids.';
      case 'count_exceeded':
        return 'Total amino acid count exceeds the limit.';
      case 'count_too_low':
        return 'Total amino acid count is below the minimum.';
      case 'too_many_components':
        return 'Number of components exceeds the limit.';
      case 'too_many_tokens':
        return 'Total token count exceeds the limit.';
      case 'insufficient_permissions':
        return 'You do not have sufficient permissions for some components.';
      default:
        assertNever(error);
        return 'Unknown validation error';
    }
  });

export function getComponentCount(components: SelectedComponents): number {
  return Object.values(components).reduce((acc, component) => acc + component.count, 0);
}

export function getPermissionValidationResult(components: SelectedComponents): ComponentValidationResult {
  const errors: ComponentValidationResult = [];
  const componentsWithInsufficientPermissions: Array<string> = [];

  for (const [componentId, component] of Object.entries(components)) {
    // Check for readable permission (always required)
    if (!component.permissions || !component.permissions.readable) {
      componentsWithInsufficientPermissions.push(componentId);
      continue;
    }

    // Check for polymerFeaturesEditable for proteins (EDIT_FEATURES_INTENT)
    // This is required to run structure prediction which modifies the protein
    if (
      component.entityType === ModelTypeConstants.PROTEIN &&
      component.permissions.polymerFeaturesEditable === false
    ) {
      componentsWithInsufficientPermissions.push(componentId);
    }
  }

  if (componentsWithInsufficientPermissions.length > 0) {
    errors.push({
      type: 'insufficient_permissions',
      componentIds: componentsWithInsufficientPermissions,
    });
  }
  return errors;
}

export function getComponentValidationResult(
  components: SelectedComponents,
  model: string
): ComponentValidationResult | null {
  const errors: ComponentValidationResult = [];
  if (Object.keys(components).length === 0) {
    errors.push({type: 'no_components'});
    return errors;
  }
  const {moleculeComponents, proteinComponents} = separateComponentsByEntityType(components);
  const moleculeComponentValidationResult = getMoleculeComponentValidationResult(moleculeComponents);
  const proteinComponentValidationResult = getProteinComponentValidationResult(proteinComponents, model);
  if (moleculeComponentValidationResult != null) {
    errors.push(...moleculeComponentValidationResult);
  }
  if (proteinComponentValidationResult != null) {
    errors.push(...proteinComponentValidationResult);
  }

  const totalTokenCount = calculateTotalTokenCount(components);
  const maxTokenCount = getMaxTokenCount(model);
  if (maxTokenCount != null && totalTokenCount > maxTokenCount) {
    errors.push({
      type: 'too_many_tokens',
      tokenCount: totalTokenCount,
    });
  }

  // Check permissions validation
  const permissionValidationResult = getPermissionValidationResult(components);
  if (permissionValidationResult != null) {
    errors.push(...permissionValidationResult);
  }

  return errors.length > 0 ? errors : null;
}

export function getMoleculeComponentValidationResult(
  components: SelectedComponents
): ComponentValidationResult | null {
  const errors: ComponentValidationResult = [];

  return errors.length > 0 ? errors : null;
}

export function getProteinComponentValidationResult(
  components: SelectedComponents,
  model: string
): ComponentValidationResult | null {
  const errors: ComponentValidationResult = [];

  if (Object.keys(components).length === 0) {
    errors.push({type: 'no_aa_sequence_components'});
    return errors;
  }

  // Check for empty amino acids in any component
  const componentsWithEmptyAminoAcids: Array<string> = [];
  for (const [componentId, component] of Object.entries(components)) {
    if (component.aminoAcids.length === 0) {
      componentsWithEmptyAminoAcids.push(componentId);
    }
  }

  if (componentsWithEmptyAminoAcids.length > 0) {
    errors.push({
      type: 'empty_amino_acids',
      componentIds: componentsWithEmptyAminoAcids,
    });
  }

  // Check for non-standard amino acids in any component
  const componentsWithNonStandardAminoAcids: Array<string> = [];
  for (const [componentId, component] of Object.entries(components)) {
    if (containsNonStandardAminoAcids(component.aminoAcids)) {
      componentsWithNonStandardAminoAcids.push(componentId);
    }
  }

  if (componentsWithNonStandardAminoAcids.length > 0) {
    errors.push({
      type: 'non_standard_amino_acids',
      componentIds: componentsWithNonStandardAminoAcids,
    });
  }

  const totalCount = calculateTotalAminoAcidCount(components);
  const minCount = getMinAminoAcidCount(model);
  if (minCount != null && totalCount < minCount) {
    errors.push({
      type: 'count_too_low',
      totalCount,
    });
  }
  if (totalCount > getMaxAminoAcidCount(model)) {
    errors.push({
      type: 'count_exceeded',
      totalCount,
    });
  }

  const componentCount = getComponentCount(components);
  if (componentCount > getMaxMultimerComponents(model)) {
    errors.push({
      type: 'too_many_components',
      componentCount,
    });
  }

  return errors.length > 0 ? errors : null;
}

export function addComponent(
  components: SelectedComponents,
  componentId: string,
  componentData: Omit<ComponentData, 'count'>
): SelectedComponents {
  const updatedComponents = {...components};

  if (updatedComponents[componentId]) {
    // Increment count of existing component
    updatedComponents[componentId] = {
      ...updatedComponents[componentId],
      count: updatedComponents[componentId].count + 1,
    };
  } else {
    // Add new component with count 1
    updatedComponents[componentId] = {...componentData, count: 1};
  }

  return updatedComponents;
}

export function addComponents(
  components: SelectedComponents,
  newComponents: Array<{
    id: string;
    aminoAcids: string;
    entityType: ModelTypeConstants;
    numHeavyAtoms: number;
    permissions: ComponentPermissions;
  }>
): SelectedComponents {
  let updatedComponents = components;

  newComponents.forEach((newComponent) => {
    updatedComponents = addComponent(updatedComponents, newComponent.id, {
      aminoAcids: newComponent.aminoAcids,
      entityType: newComponent.entityType,
      numHeavyAtoms: newComponent.numHeavyAtoms,
      permissions: newComponent.permissions,
    });
  });

  return updatedComponents;
}

export function incrementComponent(components: SelectedComponents, componentId: string): SelectedComponents {
  if (!components[componentId]) {
    return components;
  }

  return {
    ...components,
    [componentId]: {
      ...components[componentId],
      count: components[componentId].count + 1,
    },
  };
}

export function decrementComponent(components: SelectedComponents, componentId: string): SelectedComponents {
  if (!components[componentId]) {
    return components;
  }

  const component = components[componentId];
  const updatedComponents = {...components};

  if (component.count <= 1) {
    // Remove component if count reaches 0
    delete updatedComponents[componentId];
  } else {
    updatedComponents[componentId] = {
      ...component,
      count: component.count - 1,
    };
  }

  return updatedComponents;
}

export function removeComponent(components: SelectedComponents, componentId: string): SelectedComponents {
  const updatedComponents = {...components};
  delete updatedComponents[componentId];
  return updatedComponents;
}
